import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Trophy, Check } from "lucide-react";
import ApiService from "../../services/api.service";
import "../client/ClientLayout.css";
import "../coach/CoachPages.css";
import "./LeaguePages.css";

function prettyDate(iso) {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });
}

// "Round of 16" / "Quarterfinal" / "Semifinal" / "Final" — mirrors
// polyp-league's roundName() so both clients label rounds identically.
function roundName(round, totalRounds) {
  const fromEnd = totalRounds - round;
  if (fromEnd <= 0) return "Final";
  if (fromEnd === 1) return "Semifinal";
  if (fromEnd === 2) return "Quarterfinal";
  return `Round of ${2 ** (fromEnd + 1)}`;
}

function slotsByRound(tournament) {
  const byRound = {};
  for (const slot of Object.values(tournament.slots || {})) {
    (byRound[slot.round] ||= []).push(slot);
  }
  return Object.keys(byRound)
    .map(Number)
    .sort((a, b) => a - b)
    .map((round) => ({ round, slots: byRound[round].sort((a, b) => a.index - b.index) }));
}

// Fisher-Yates, then fill every match's FIRST slot before any second slot —
// guarantees no bye-vs-bye match. Client-side preview only (the server's
// own randomizePositions is authoritative on save) so a "Randomize" tap can
// be reviewed/edited before "Save Bracket" persists it.
function randomizePositions(teamIds, size) {
  const shuffled = [...teamIds];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  const matches = size / 2;
  const positions = new Array(size).fill(null);
  for (let i = 0; i < matches; i++) positions[2 * i] = shuffled[i] ?? null;
  for (let i = 0; i < matches; i++) positions[2 * i + 1] = shuffled[matches + i] ?? null;
  return positions;
}

function SeedModal({ tournament, teams, onClose, onSaved }) {
  const participantTeams = teams.filter((t) => tournament.teamIds.includes(t.teamId));
  const [positions, setPositions] = useState(
    tournament.positions && tournament.positions.length === tournament.size
      ? tournament.positions
      : new Array(tournament.size).fill(null)
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const availableTeams = (index) => {
    const placedElsewhere = new Set(positions.filter((_, i) => i !== index));
    return participantTeams.filter((t) => !placedElsewhere.has(t.teamId));
  };

  const randomize = () => setPositions(randomizePositions(tournament.teamIds, tournament.size));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await ApiService.updateLeagueTournament(tournament.tournamentId, { seed: { mode: "manual", positions } });
      onSaved();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const matchCount = positions.length / 2;

  return (
    <div className="coach-modal-backdrop" onClick={onClose}>
      <div className="coach-modal league-seed-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="client-h2">Seed Bracket</h2>
        <button className="client-button-secondary league-randomize-btn" onClick={randomize} disabled={saving}>
          Randomize
        </button>
        <div className="league-seed-matches">
          {Array.from({ length: matchCount }, (_, i) => i).map((i) => (
            <div key={i} className="league-seed-match">
              {[2 * i, 2 * i + 1].map((posIndex) => (
                <select
                  key={posIndex}
                  className="client-input"
                  value={positions[posIndex] || ""}
                  onChange={(e) => {
                    const next = [...positions];
                    next[posIndex] = e.target.value || null;
                    setPositions(next);
                  }}
                >
                  <option value="">— Bye —</option>
                  {availableTeams(posIndex).map((t) => (
                    <option key={t.teamId} value={t.teamId}>{t.name}</option>
                  ))}
                </select>
              ))}
            </div>
          ))}
        </div>
        {error && <div className="client-error">{error}</div>}
        <div className="coach-modal-actions">
          <button className="client-button-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="client-button" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save Bracket"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SlotCard({ slot, games, onSetWinner }) {
  // There's no per-game detail route on web (LeagueSchedulePage's GameRow
  // just expands inline in place), so a linked game surfaces its status
  // here rather than navigating anywhere — assigning an official or
  // deleting the game itself still happens from the Schedule tab, which the
  // tournament round badge (see LeagueSchedulePage) makes easy to spot.
  const game = games.find((g) => g.gameId === slot.gameId);
  const canOverride = slot.teamAId && slot.teamBId;

  const teamRow = (name, present, isWinner) => (
    <div className="league-slot-team">
      <span className={present ? "" : "league-slot-tbd"}>{present ? (name || "Unnamed Team") : "TBD"}</span>
      {isWinner && <Check size={14} className="league-slot-winner-check" />}
    </div>
  );

  return (
    <div className={`league-slot${slot.tie ? " league-slot-tied" : ""}`}>
      {teamRow(slot.teamAName, !!slot.teamAId, slot.winnerTeamId && slot.winnerTeamId === slot.teamAId)}
      {teamRow(slot.teamBName, !!slot.teamBId, slot.winnerTeamId && slot.winnerTeamId === slot.teamBId)}
      {slot.tie && <div className="league-slot-tie-note">Tied — set a winner</div>}
      {game && !slot.winnerTeamId && (
        <div className="league-slot-status">{game.officialName ? `Assigned: ${game.officialName}` : "No official assigned"}</div>
      )}
      {canOverride && (
        <div className="league-slot-actions">
          {slot.teamAId && (
            <button className="league-slot-override" onClick={() => onSetWinner(slot, slot.teamAId)}>
              {slot.teamAName || "Team A"} wins
            </button>
          )}
          {slot.teamBId && (
            <button className="league-slot-override" onClick={() => onSetWinner(slot, slot.teamBId)}>
              {slot.teamBName || "Team B"} wins
            </button>
          )}
          {slot.winnerSource === "manual" && (
            <button className="league-remove-team" onClick={() => onSetWinner(slot, null)}>Clear Override</button>
          )}
        </div>
      )}
    </div>
  );
}

export default function LeagueTournamentPage() {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState(null);
  const [teams, setTeams] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSeed, setShowSeed] = useState(false);
  const [actionError, setActionError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [tResp, gResp] = await Promise.all([
        ApiService.getLeagueTournament(tournamentId),
        ApiService.getLeagueGames("upcoming"),
      ]);
      setTournament(tResp);
      setGames(gResp.games || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [tournamentId]);

  useEffect(() => {
    (async () => {
      try {
        const resp = await ApiService.getLeagueTeams();
        setTeams(resp.teams || []);
      } catch {
        // seed modal just shows no teams
      }
    })();
  }, []);

  // Reload every time this page is visited (not just once) — the server
  // reconciles the bracket (advances winners, mints the next round's game)
  // on every GET, and returning here after a game finishes is exactly when
  // that reconcile needs to run again.
  useEffect(() => {
    load();
  }, [load]);

  const setWinner = async (slot, winnerTeamId) => {
    setActionError(null);
    try {
      await ApiService.updateLeagueTournament(tournamentId, {
        slotWinner: { slotKey: `R${slot.round}S${slot.index}`, winnerTeamId },
      });
      await load();
    } catch (err) {
      setActionError(err.message);
    }
  };

  const deleteTournament = async () => {
    if (!window.confirm("Delete this tournament? Games already played are kept in the schedule; games not yet played are removed.")) return;
    try {
      await ApiService.deleteLeagueTournament(tournamentId);
      navigate("/league/schedule");
    } catch (err) {
      setActionError(err.message);
    }
  };

  if (loading && !tournament) {
    return <div className="client-loading-row">Loading…</div>;
  }
  if (error && !tournament) {
    return <div className="client-error">{error}</div>;
  }
  if (!tournament) return null;

  const totalRounds = tournament.size > 1 ? Math.round(Math.log2(tournament.size)) : 1;

  return (
    <div>
      <Link to="/league/schedule" className="coach-back-link">
        <ArrowLeft size={16} /> Schedule
      </Link>

      <div className="coach-page-header">
        <div>
          <h1 className="client-h1">{tournament.name || "Untitled Tournament"}</h1>
          {tournament.status === "completed" && tournament.championTeamName ? (
            <p className="client-subtitle league-champion-line">
              <Trophy size={16} /> {tournament.championTeamName} wins
            </p>
          ) : tournament.startDate ? (
            <p className="client-subtitle">{prettyDate(tournament.startDate)}</p>
          ) : null}
        </div>
        {tournament.status === "active" && (
          <button className="client-button-secondary" onClick={() => setShowSeed(true)}>Re-seed</button>
        )}
      </div>

      {tournament.status === "seeding" ? (
        <div className="league-seed-cta">
          <p className="client-subtitle">This bracket is empty — seed it to create the first round's games.</p>
          <button className="client-button" onClick={() => setShowSeed(true)}>Seed Bracket</button>
        </div>
      ) : (
        <div className="league-bracket">
          {slotsByRound(tournament).map((column) => (
            <div key={column.round} className="league-bracket-round">
              <div className="league-bracket-round-label">{roundName(column.round, totalRounds)}</div>
              {column.slots.map((slot) => (
                <SlotCard
                  key={`${slot.round}-${slot.index}`}
                  slot={slot}
                  games={games}
                  onSetWinner={setWinner}
                />
              ))}
            </div>
          ))}
        </div>
      )}

      {actionError && <div className="client-error">{actionError}</div>}

      <button className="league-remove-team" onClick={deleteTournament} style={{ marginTop: 24 }}>
        Delete Tournament
      </button>

      {showSeed && (
        <SeedModal
          tournament={tournament}
          teams={teams}
          onClose={() => setShowSeed(false)}
          onSaved={() => { setShowSeed(false); load(); }}
        />
      )}
    </div>
  );
}
